import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { marketplaceApi } from "../api/client";
import MarketplaceHeader from "../components/MarketplaceHeader";
import ProductCard from "../components/ProductCard";
import TransporterServiceCard from "../components/TransporterServiceCard";
import FloatingTools from "../components/FloatingTools";
import Footer from "../components/Footer";

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const tab = params.get("tab") || "all";
  const [query, setQuery] = useState(q);
  const [payload, setPayload] = useState({ products: [], categories: [], sellers: [], transporter_services: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    marketplaceApi.search({ q, tab })
      .then((res) => { if (mounted) setPayload(res.data || {}); })
      .catch(() => { if (mounted) setError("Search failed to load right now."); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [q, tab]);

  const total = useMemo(() => Object.values(payload.counts || {}).reduce((sum, value) => sum + Number(value || 0), 0), [payload.counts]);

  function runSearch(event) {
    event.preventDefault();
    if (!query.trim()) return;
    setParams({ q: query.trim(), tab });
  }

  const tabs = ["all", "Products", "Suppliers", "Transporters"];

  return (
    <div className="page-shell marketplace-page search-results-page" id="top">
      <MarketplaceHeader apiOnline />
      <main className="container search-results-shell">
        <section className="marketplace-title-banner marketplace-title-banner--search">
          <img src="/images/tazy-marketplace-banner.png" alt="" />
          <div className="marketplace-title-banner__content">
            <span>TAZY MARKETPLACE SEARCH</span>
            <h1>Search products, sellers and transporters.</h1>
            <p>{loading ? "Searching live marketplace data..." : `${total} result(s) for “${q || "all"}”.`}</p>
            <form onSubmit={runSearch} className="marketplace-title-search">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products, sellers or transporters" />
              <button type="submit">Search</button>
            </form>
          </div>
        </section>

        <div className="search-filter-tabs">
          {tabs.map((item) => <button type="button" key={item} className={tab === item ? "active" : ""} onClick={() => setParams({ q, tab: item })}>{item === "all" ? "All" : item}</button>)}
        </div>

        {error && <div className="error-box workspace-alert">{error}</div>}

        <section className="search-section-block">
          <div className="section-heading"><div><h2>Products</h2><p>Published and available listings matching your search.</p></div><b>{payload.counts?.products || 0}</b></div>
          <div className="product-grid product-grid--compact search-product-grid">
            {payload.products?.length ? payload.products.map((item) => <ProductCard key={item.id} item={item} />) : <div className="marketplace-empty-feed">No matching products.</div>}
          </div>
        </section>

        <section className="search-section-block search-two-col">
          <div>
            <div className="section-heading"><div><h2>Categories</h2><p>Category matches.</p></div><b>{payload.counts?.categories || 0}</b></div>
            <div className="search-pill-list">
              {payload.categories?.length ? payload.categories.map((cat) => <Link key={cat.id} to={`/categories/${cat.code}`}>{cat.name}<span>›</span></Link>) : <em>No matching categories.</em>}
            </div>
          </div>
          <div>
            <div className="section-heading"><div><h2>Verified sellers</h2><p>Approved storefronts that match the search.</p></div><b>{payload.counts?.sellers || 0}</b></div>
            <div className="search-seller-list">
              {payload.sellers?.length ? payload.sellers.map((seller) => <article key={seller.id}><strong>{seller.store_name || seller.business_name || "Verified seller"}</strong><span>{seller.city || "Zimbabwe"} • {seller.status || "VERIFIED"}</span><Link to={`/search?q=${encodeURIComponent(seller.store_name || seller.business_name || "")}&tab=Products`}>View listings</Link></article>) : <em>No matching sellers.</em>}
            </div>
          </div>
        </section>

        <section className="search-section-block">
          <div className="section-heading"><div><h2>Transporter services</h2><p>Delivery services that match your route, area or service type.</p></div><b>{payload.counts?.transporter_services || 0}</b></div>
          <div className="transport-services-market-grid transport-services-market-grid--home">
            {payload.transporter_services?.length ? payload.transporter_services.map((service) => <TransporterServiceCard key={service.id} service={service} compact />) : <div className="marketplace-empty-feed">No matching transport services.</div>}
          </div>
        </section>
      </main>
      <Footer />
      <FloatingTools />
    </div>
  );
}
